import type { LiveOmrScanResult } from '@/lib/omr-api';
import type { QuestionListItem, TeacherQuestionSetListItem } from '@/lib/question-sets';
import type { StudentListItem } from '@/lib/students-admin';
import { supabase } from '@/lib/supabase';

const CREATE_OMR_BATCH_FUNCTION = 'create-omr-result-batch';
const PERSIST_OMR_SHEET_FUNCTION = 'persist-omr-result-sheet';
const FETCH_TEACHER_OMR_RESULTS_FUNCTION = 'teacher-fetch-omr-results';

function getFunctionErrorStatus(error: unknown) {
  const context = (error as { context?: { status?: unknown } } | null)?.context;
  return typeof context?.status === 'number' ? context.status : null;
}

export type OmrSavedAnswer = {
  answer_status: 'marked' | 'blank' | 'multi_marked' | 'ambiguous';
  confidence: number;
  correct_option: 'A' | 'B' | 'C' | 'D' | null;
  detected_option: 'A' | 'B' | 'C' | 'D' | null;
  final_option: 'A' | 'B' | 'C' | 'D' | null;
  id: string;
  is_correct: boolean;
  question_id: string | null;
  question_number: number;
};

export type TeacherOmrResultSheet = {
  answers: OmrSavedAnswer[];
  backend_status: 'passed' | 'review_required' | 'rejected';
  class_label: string;
  created_at: string;
  extracted_roll_number: string | null;
  extracted_student_name: string | null;
  id: string;
  issue_count: number;
  matched_student_name: string | null;
  overlay_url: string | null;
  percent: number;
  quality_issues: Array<Record<string, unknown>>;
  question_set_id: string;
  review_notes: string | null;
  score: number;
  source_filename: string;
  subject_label: string;
  total_marks: number;
  uploaded_at: string | null;
  student_name: string;
  student_roll: string | null;
  summary: {
    blank: number;
    correct: number;
    incorrect: number;
  };
  test_title: string;
};

export async function createOmrResultBatch(input: {
  actorTeacherId?: string | null;
  backendJobId: string;
  selectedSet: Pick<
    TeacherQuestionSetListItem,
    'class_id' | 'id' | 'question_count' | 'school_id' | 'subject_id' | 'teacher_id'
  >;
  sheetCount: number;
  totalStudents: number;
}) {
  try {
    const { data, error } = await supabase
      .from('omr_result_batches')
      .insert({
        backend_job_id: input.backendJobId,
        class_id: input.selectedSet.class_id,
        question_count: input.selectedSet.question_count,
        question_set_id: input.selectedSet.id,
        school_id: input.selectedSet.school_id,
        sheet_count: input.sheetCount,
        subject_id: input.selectedSet.subject_id,
        teacher_id: input.actorTeacherId ?? input.selectedSet.teacher_id,
        total_students: input.totalStudents,
      })
      .select('id, backend_job_id')
      .single();

    if (error) {
      throw error;
    }

    if (data?.id) {
      return data as {
        backend_job_id: string;
        id: string;
      };
    }
  } catch (directInsertError) {
    console.error('Direct OMR batch insert failed, falling back to edge function:', directInsertError);
  }

  let data: any;
  let error: any;

  try {
    const response = await supabase.functions.invoke(CREATE_OMR_BATCH_FUNCTION, {
      body: {
        backendJobId: input.backendJobId,
        classId: input.selectedSet.class_id,
        questionCount: input.selectedSet.question_count,
        questionSetId: input.selectedSet.id,
        schoolId: input.selectedSet.school_id,
        sheetCount: input.sheetCount,
        subjectId: input.selectedSet.subject_id,
        totalStudents: input.totalStudents,
      },
    });
    data = response.data;
    error = response.error;
  } catch (invokeError) {
    throw new Error(
      `Unable to reach Supabase function ${CREATE_OMR_BATCH_FUNCTION}. Check deployment, auth session, or network.`,
    );
  }

  if (error) {
    throw error;
  }

  if (!data?.batch?.id) {
    throw new Error('OMR batch could not be created.');
  }

  return data.batch as {
    backend_job_id: string;
    id: string;
  };
}

export async function persistOmrResultSheet(input: {
  batchId: string;
  questions: QuestionListItem[];
  result: LiveOmrScanResult;
  sourceFilename: string;
  students: StudentListItem[];
  schoolId: string;
}) {
  try {
    const matchedStudent =
      input.result.student?.roll?.trim()
        ? input.students.find(
            (student) =>
              student.roll_number.trim().toLowerCase() ===
              input.result.student!.roll!.trim().toLowerCase(),
          ) ?? null
        : null;

    const backendSheetId = input.result.job_id?.trim() || `local-${input.sourceFilename}`;
    const score = Number(input.result.evaluation?.score ?? 0);
    const totalMarks = Number(input.result.evaluation?.total ?? 0);
    const percent =
      totalMarks > 0 && Number.isFinite(score) ? Number(((score / totalMarks) * 100).toFixed(2)) : 0;

    const { data: existingSheet, error: existingSheetError } = await supabase
      .from('omr_result_sheets')
      .select('id')
      .eq('batch_id', input.batchId)
      .eq('backend_sheet_id', backendSheetId)
      .maybeSingle();

    if (existingSheetError) {
      throw existingSheetError;
    }

    const sheetPayload = {
      backend_sheet_id: backendSheetId,
      backend_status: mapBackendStatus(input.result.status),
      batch_id: input.batchId,
      extracted_roll_number: input.result.student?.roll?.trim() || null,
      extracted_student_name: input.result.student?.name?.trim() || null,
      overlay_url: input.result.debug?.bubble_overlay_url ?? null,
      matched_student_id: matchedStudent?.id ?? null,
      issue_count: buildQualityIssues(input.result).length,
      percent,
      quality_issues: buildQualityIssues(input.result),
      result_payload: input.result,
      review_notes:
        input.result.status === 'no_qr' || input.result.status === 'error'
          ? input.result.message ?? null
          : null,
      school_id: input.schoolId,
      score,
      source_filename: input.sourceFilename,
      total_marks: totalMarks,
      uploaded_at: input.result.scanned_at ?? new Date().toISOString(),
    };

    let sheetId: string;

    if (existingSheet?.id) {
      const { data: updatedSheet, error: updateError } = await supabase
        .from('omr_result_sheets')
        .update(sheetPayload)
        .eq('id', existingSheet.id)
        .select('id')
        .single();

      if (updateError || !updatedSheet) {
        throw updateError || new Error('Unable to update OMR sheet.');
      }

      sheetId = updatedSheet.id;

      const { error: deleteAnswersError } = await supabase
        .from('omr_result_answers')
        .delete()
        .eq('sheet_result_id', sheetId);

      if (deleteAnswersError) {
        throw deleteAnswersError;
      }
    } else {
      const { data: insertedSheet, error: insertError } = await supabase
        .from('omr_result_sheets')
        .insert(sheetPayload)
        .select('id')
        .single();

      if (insertError || !insertedSheet) {
        throw insertError || new Error('Unable to create OMR sheet.');
      }

      sheetId = insertedSheet.id;
    }

    const questionMap = new Map(input.questions.map((question) => [question.position, question] as const));

    const answerRows = (input.result.omr?.questions ?? [])
      .filter((answer) => typeof answer.question_number === 'number' && answer.question_number > 0)
      .map((answer) => {
        const question = questionMap.get(answer.question_number);
        const detectedOption = normalizeOption(answer.selected_option);
        const correctOption = normalizeOption(question?.correct_option ?? null);
        const answerStatus = normalizeAnswerStatus(answer.status);

        return {
          answer_status: answerStatus,
          confidence: Number(answer.confidence ?? 0),
          correct_option: correctOption,
          detected_option: detectedOption,
          final_option: detectedOption,
          is_correct:
            answerStatus === 'marked' &&
            detectedOption !== null &&
            correctOption !== null &&
            detectedOption === correctOption,
          question_id: question?.id ?? null,
          question_number: answer.question_number,
          school_id: input.schoolId,
          sheet_result_id: sheetId,
        };
      });

    if (answerRows.length > 0) {
      const { error: answersError } = await supabase.from('omr_result_answers').insert(answerRows);
      if (answersError) {
        throw answersError;
      }
    }

    return {
      answersInserted: answerRows.length,
      matchedStudent: matchedStudent
        ? {
            id: matchedStudent.id,
            roll_number: matchedStudent.roll_number,
          }
        : null,
      sheet: {
        id: sheetId,
      },
    };
  } catch (directSaveError) {
    console.error('Direct OMR sheet persistence failed, falling back to edge function:', directSaveError);
  }

  let data: any;
  let error: any;

  try {
    const response = await supabase.functions.invoke(PERSIST_OMR_SHEET_FUNCTION, {
      body: {
        batchId: input.batchId,
        result: input.result,
        sourceFilename: input.sourceFilename,
      },
    });
    data = response.data;
    error = response.error;
  } catch (invokeError) {
    throw new Error(
      `Unable to reach Supabase function ${PERSIST_OMR_SHEET_FUNCTION}. Check deployment, auth session, or network.`,
    );
  }

  if (error) {
    throw error;
  }

  if (!data?.sheet?.id) {
    throw new Error('OMR sheet result could not be persisted.');
  }

  return data as {
    answersInserted: number;
    matchedStudent: {
      id: string;
      roll_number: string;
    } | null;
    sheet: {
      id: string;
    };
  };
}

export async function fetchTeacherOmrResults(teacherId: string, schoolId: string) {
  try {
    const { data, error } = await supabase.functions.invoke(FETCH_TEACHER_OMR_RESULTS_FUNCTION, {
      body: {},
    });

    if (error) {
      throw error;
    }

    return (data?.results ?? []) as TeacherOmrResultSheet[];
  } catch (functionError) {
    if (getFunctionErrorStatus(functionError) !== null) {
      throw functionError;
    }
    console.warn('Teacher OMR results function unavailable, falling back to direct query:', functionError);
  }

  const { data: assignments, error: assignmentsError } = await supabase
    .from('class_subjects')
    .select('class_id, subject_id')
    .eq('teacher_id', teacherId)
    .eq('school_id', schoolId);

  if (assignmentsError) {
    throw assignmentsError;
  }

  const assignmentRows = assignments ?? [];
  if (assignmentRows.length === 0) {
    return [] as TeacherOmrResultSheet[];
  }

  const assignedKeys = new Set(
    assignmentRows.map((assignment) => buildAssignmentKey(schoolId, assignment.class_id, assignment.subject_id))
  );
  const classIds = [...new Set(assignmentRows.map((assignment) => assignment.class_id))];
  const subjectIds = [...new Set(assignmentRows.map((assignment) => assignment.subject_id))];

  const { data: batches, error: batchesError } = await supabase
    .from('omr_result_batches')
    .select(`
      id,
      created_at,
      question_set_id,
      class_id,
      subject_id,
      school_id,
      question_sets (title),
      classes (name, section),
      subjects (name, code)
    `)
    .eq('school_id', schoolId)
    .in('class_id', classIds)
    .in('subject_id', subjectIds)
    .order('created_at', { ascending: false });

  if (batchesError) {
    throw batchesError;
  }

  const batchRows = ((batches ?? []) as any[]).filter((batch) =>
    assignedKeys.has(buildAssignmentKey(batch.school_id, batch.class_id, batch.subject_id))
  );
  if (batchRows.length === 0) {
    return [] as TeacherOmrResultSheet[];
  }

  const batchMap = new Map(
    batchRows.map((batch) => [
      batch.id,
      {
        class_label: batch.classes?.section?.trim()
          ? `${batch.classes.name} - ${batch.classes.section}`
          : batch.classes?.name ?? 'Class unavailable',
        question_set_id: batch.question_set_id,
        subject_label: batch.subjects?.code?.trim()
          ? `${batch.subjects.name} (${batch.subjects.code})`
          : batch.subjects?.name ?? 'Subject unavailable',
        test_title: batch.question_sets?.title ?? 'OMR result',
      },
    ] as const),
  );

  const batchIds = batchRows.map((batch) => batch.id);

  const { data: sheets, error: sheetsError } = await supabase
    .from('omr_result_sheets')
    .select(`
      id,
      batch_id,
      source_filename,
      backend_status,
      uploaded_at,
      issue_count,
      overlay_url,
      extracted_student_name,
      extracted_roll_number,
      review_notes,
      quality_issues,
      matched_student_id,
      score,
      total_marks,
      percent,
      created_at
    `)
    .in('batch_id', batchIds)
    .order('created_at', { ascending: false });

  if (sheetsError) {
    throw sheetsError;
  }

  const sheetRows = (sheets ?? []) as any[];
  if (sheetRows.length === 0) {
    return [] as TeacherOmrResultSheet[];
  }

  const sheetIds = sheetRows.map((sheet) => sheet.id);

  const { data: answers, error: answersError } = await supabase
    .from('omr_result_answers')
    .select(`
      id,
      sheet_result_id,
      question_id,
      question_number,
      answer_status,
      detected_option,
      final_option,
      correct_option,
      is_correct,
      confidence
    `)
    .in('sheet_result_id', sheetIds)
    .order('question_number', { ascending: true });

  if (answersError) {
    throw answersError;
  }

  const answersBySheet = new Map<string, OmrSavedAnswer[]>();
  for (const row of (answers ?? []) as any[]) {
    const current = answersBySheet.get(row.sheet_result_id) ?? [];
    current.push({
      answer_status: row.answer_status,
      confidence: Number(row.confidence ?? 0),
      correct_option: row.correct_option,
      detected_option: row.detected_option,
      final_option: row.final_option,
      id: row.id,
      is_correct: Boolean(row.is_correct),
      question_id: row.question_id,
      question_number: row.question_number,
    });
    answersBySheet.set(row.sheet_result_id, current);
  }

  const matchedStudentIds = sheetRows
    .map((sheet) => sheet.matched_student_id)
    .filter((value): value is string => Boolean(value));

  const matchedStudentsMap = new Map<
    string,
    {
      full_name: string | null;
      roll_number: string;
    }
  >();

  if (matchedStudentIds.length > 0) {
    const { data: students, error: studentsError } = await supabase
      .from('students')
      .select('id, profile_id, roll_number')
      .in('id', matchedStudentIds);

    if (studentsError) {
      throw studentsError;
    }

    const profileIds = ((students ?? []) as any[])
      .map((student) => student.profile_id)
      .filter((value: string | null) => Boolean(value));

    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', profileIds);

    if (profilesError) {
      throw profilesError;
    }

    const profileMap = new Map(((profiles ?? []) as any[]).map((profile) => [profile.id, profile] as const));

    for (const student of (students ?? []) as any[]) {
      matchedStudentsMap.set(student.id, {
        full_name: profileMap.get(student.profile_id)?.full_name ?? null,
        roll_number: student.roll_number,
      });
    }
  }

  return sheetRows.map((sheet) => {
    const batch = batchMap.get(sheet.batch_id);
    const matchedStudent = sheet.matched_student_id
      ? matchedStudentsMap.get(sheet.matched_student_id) ?? null
      : null;
    const sheetAnswers = answersBySheet.get(sheet.id) ?? [];
    const summary = sheetAnswers.reduce(
      (acc, answer) => {
        if (answer.answer_status === 'blank') {
          acc.blank += 1;
        } else if (answer.is_correct) {
          acc.correct += 1;
        } else {
          acc.incorrect += 1;
        }
        return acc;
      },
      { blank: 0, correct: 0, incorrect: 0 },
    );

    return {
      answers: sheetAnswers,
      backend_status: sheet.backend_status,
      class_label: batch?.class_label ?? 'Class unavailable',
      created_at: sheet.created_at,
      extracted_roll_number: sheet.extracted_roll_number ?? null,
      extracted_student_name: sheet.extracted_student_name ?? null,
      id: sheet.id,
      issue_count: Number(sheet.issue_count ?? 0),
      matched_student_name: matchedStudent?.full_name ?? null,
      overlay_url: sheet.overlay_url ?? null,
      percent: Number(sheet.percent ?? 0),
      quality_issues: Array.isArray(sheet.quality_issues) ? sheet.quality_issues : [],
      question_set_id: batch?.question_set_id ?? '',
      review_notes: sheet.review_notes ?? null,
      score: Number(sheet.score ?? 0),
      source_filename: sheet.source_filename,
      student_name:
        matchedStudent?.full_name?.trim() ||
        sheet.extracted_student_name?.trim() ||
        (sheet.extracted_roll_number ? `Student ${sheet.extracted_roll_number}` : 'Student unavailable'),
      student_roll: matchedStudent?.roll_number ?? sheet.extracted_roll_number ?? null,
      subject_label: batch?.subject_label ?? 'Subject unavailable',
      summary,
      test_title: batch?.test_title ?? 'OMR result',
      total_marks: Number(sheet.total_marks ?? 0),
      uploaded_at: sheet.uploaded_at ?? null,
    } satisfies TeacherOmrResultSheet;
  });
}

function buildAssignmentKey(schoolId: string, classId: string, subjectId: string) {
  return `${schoolId}:${classId}:${subjectId}`;
}

function mapBackendStatus(status: LiveOmrScanResult['status']) {
  if (status === 'success') return 'passed';
  if (status === 'no_qr') return 'review_required';
  return 'rejected';
}

function normalizeAnswerStatus(status: string | null | undefined) {
  if (status === 'marked' || status === 'blank' || status === 'multi_marked' || status === 'ambiguous') {
    return status;
  }
  return 'blank';
}

function normalizeOption(option: string | null | undefined): 'A' | 'B' | 'C' | 'D' | null {
  if (option === 'A' || option === 'B' || option === 'C' || option === 'D') {
    return option;
  }
  return null;
}

function buildQualityIssues(result: LiveOmrScanResult) {
  const issues: Array<Record<string, unknown>> = [];

  if (result.debug?.failure_reason) {
    issues.push({
      code: result.debug.failure_reason,
      type: 'failure_reason',
    });
  }

  const summary = result.omr?.summary;
  if (summary?.ambiguous_count) {
    issues.push({
      count: summary.ambiguous_count,
      type: 'ambiguous_count',
    });
  }

  if (summary?.multi_marked_count) {
    issues.push({
      count: summary.multi_marked_count,
      type: 'multi_marked_count',
    });
  }

  if (result.status === 'no_qr') {
    issues.push({
      message: result.message ?? 'QR could not be resolved from the sheet.',
      type: 'review_required',
    });
  }

  return issues;
}
